import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TargetDTO } from '@bb/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { useLiveEvent } from '@/lib/live';

export function useTargets() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.targets,
    queryFn: async () => (await api.targets.list()).targets,
  });

  useLiveEvent('target.upserted', (target) => {
    queryClient.setQueryData<TargetDTO[]>(queryKeys.targets, (current) => {
      if (!current) return [target];
      const exists = current.some((item) => item.id === target.id);
      return exists
        ? current.map((item) => (item.id === target.id ? target : item))
        : [...current, target];
    });
  });

  useLiveEvent('target.deleted', ({ id }) => {
    queryClient.setQueryData<TargetDTO[]>(queryKeys.targets, (current) =>
      current?.filter((item) => item.id !== id),
    );
  });

  return query;
}

export function useTargetActions() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.targets });
    void queryClient.invalidateQueries({ queryKey: queryKeys.status });
  };

  const create = useMutation({
    mutationFn: (input: { handle: string; color?: string | null; notes?: string | null }) =>
      api.targets.create(input),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { isActive?: boolean; color?: string | null; notes?: string | null };
    }) => api.targets.update(id, patch),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: ({ id, confirm }: { id: string; confirm: boolean }) =>
      api.targets.remove(id, confirm),
    onSuccess: () => {
      invalidate();
      // Posts vanish with their target, so any cached page is now wrong.
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  return { create, update, remove };
}
